import { isNullish } from '@adornis/assert/isNullish.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { EMP_INFO_SYSTEM_NAME } from '@martinus/taetigkeit/constants/StandardEmploymentInformationSystemName.js';
import { isLehrkraft } from '@martinus/taetigkeit/enums/Taetigkeiten.js';
import {
  isVertragsformBefristet,
  isVertragsformFullTime,
  isVertragsformPartTime,
  isVertragsformUnbefristet,
  Vertragsform,
} from '@martinus/taetigkeit/enums/Vertragsform.js';
import { FULL_TIME_WORKING_HOURS } from '../constants/fullTimeWorkingHours.js';
import type { AppliedContract } from '../entities/AppliedContract.js';
import type { Contract } from '../entities/Contract.js';
import type { AGEingruppierung } from '../entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';

/**
 * Calculates the working hours for a single eingruppierung
 * @param eingruppierung The eingruppierung to calculate the working hours for
 * @param ignoreFerienDivisor If true, the ferienDivisor will be ignored. Usefull for showing the working hours which actually need to be done instead of getting paid
 * @returns The working hours for the given eingruppierung
 */
export function calculateWorkingHoursForEingruppierung(
  eingruppierung: NarrowedEntity<
    AGEingruppierung,
    {
      agArbeitszeit: { ferienEinarbeiten: 1; zaehler: 1; maxDeputat: 1; stunden: 1 };
      agVertragsInfos: { taetigkeit: 1 };
    }
  >,
  ignoreFerienDivisor = false,
  baseWorkingHours = FULL_TIME_WORKING_HOURS,
): number {
  const workingData = eingruppierung.agArbeitszeit;
  if (!workingData) return 0.0;

  // We actually need to substract 12.5% of the working hours if someone is already working in holidays. Its just the law or something.
  const ferienDivisor = workingData.ferienEinarbeiten && !ignoreFerienDivisor ? 1.125 : 1;

  // we need other behavior if someone is a lehrkraft or a pädagogische fachkraft
  const nenner = isLehrkraft(eingruppierung.agVertragsInfos?.taetigkeit)
    ? (workingData.maxDeputat ?? 0)
    : (workingData.stunden ?? 0);

  /**
   * Edgecase, normally a nenner should always be existing and not 0
   */
  if (!nenner) return (workingData.zaehler ?? baseWorkingHours) / ferienDivisor;

  // If someone has no zaehler on how many hours he works we just take a fulltime workweek
  if (isNullish(workingData.zaehler) || Number.isNaN(workingData.zaehler)) return baseWorkingHours / ferienDivisor;
  return (workingData.zaehler / ferienDivisor / nenner) * baseWorkingHours;
}

/**
 * Since an employee can have two eingruppierungen with different hours and calculations we need to calculate them both independently and the sum up the results
 * @param contract The contract to calculate the working hours for
 * @returns The total workingHours in a week for the contract as a number
 */
export function calculateWorkingHoursForVertrag(
  contract: NarrowedEntity<
    Contract | AppliedContract,
    {
      agEingruppierung1: {
        agVertragsInfos: { taetigkeit: 1 };
        agArbeitszeit: { ferienEinarbeiten: 1; zaehler: 1; maxDeputat: 1; stunden: 1 };
      };
      agEingruppierung2: {
        agVertragsInfos: { taetigkeit: 1 };
        agArbeitszeit: { ferienEinarbeiten: 1; zaehler: 1; maxDeputat: 1; stunden: 1 };
      };
    }
  >,
  baseWorkingHours = FULL_TIME_WORKING_HOURS,
): number {
  let workingHours = 0.0;
  workingHours += contract.agEingruppierung1
    ? calculateWorkingHoursForEingruppierung(contract.agEingruppierung1, false, baseWorkingHours)
    : 0.0;
  workingHours += contract.agEingruppierung2
    ? calculateWorkingHoursForEingruppierung(contract.agEingruppierung2, false, baseWorkingHours)
    : 0.0;

  return Math.round(workingHours * 100) / 100;
}

/**
 * Depending on different parameters we can determine if a contract is fulltime or parttime and if it has a term limit or not.
 * We want this information to be displayed in the UI so we build a string that represents the form of the contract.
 * @param contract
 * @returns A string that represents the form of the contract
 */
export function getContractForm(
  contract: NarrowedEntity<
    Contract | AppliedContract,
    {
      agInfos: { gekuendigtZum: 1 };
      agEingruppierung1: {
        agVertragsInfos: { taetigkeit: 1 };
        agArbeitszeit: { ferienEinarbeiten: 1; zaehler: 1; maxDeputat: 1; stunden: 1 };
      };
      agEingruppierung2: {
        agVertragsInfos: { taetigkeit: 1 };
        agArbeitszeit: { ferienEinarbeiten: 1; zaehler: 1; maxDeputat: 1; stunden: 1 };
      };
    }
  >,
): (typeof Vertragsform)[number] | undefined {
  let possibleForms = Vertragsform.map(form => form[EMP_INFO_SYSTEM_NAME]);

  const hours = calculateWorkingHoursForVertrag(contract);
  possibleForms =
    hours === FULL_TIME_WORKING_HOURS
      ? possibleForms.filter(isVertragsformFullTime)
      : possibleForms.filter(isVertragsformPartTime);

  possibleForms = contract.agInfos?.gekuendigtZum
    ? possibleForms.filter(isVertragsformBefristet)
    : possibleForms.filter(isVertragsformUnbefristet);

  return Vertragsform.filter(form => possibleForms.includes(form[EMP_INFO_SYSTEM_NAME]))?.at(0);
}
